import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';
import { PI } from '../../constants-types';
import {
    PartnerInformation,
    PartnerInformationServer,
    ClassificationType
} from '../../core-data-model';
import {
    ApiService,
    InitDataService,
    ModuleAccessService,
    UserProfileSettingsService
} from '../../system-services';
import { PartnerInformationTransformer } from './partner-information.transform';

@Injectable({ providedIn: 'root' })
export class PartnerInformationApi {
    constructor(
        private api: ApiService,
        private userProfile: UserProfileSettingsService,
        private transform: PartnerInformationTransformer,
        private initData: InitDataService,
        private moduleAccess: ModuleAccessService
    ) {}

    /**
     * Fetches partner information for the current company.
     * The company ID is determined based on the user's current company or the user's company ID
     * if the classification type is DynamicDemo or Workshop.
     *
     * This is deprecated and should only be used for legacy purposes.
     * In PI2, the v5 API should be used instead.
     *
     * @returns A promise that resolves to PartnerInformation.
     */
    async getPartnerInformation(): Promise<PartnerInformation> {
        let companyId = this.userProfile.currentCompany;
        const classificationType =
            this.moduleAccess.moduleAccessConfig.accountClassification
                .classificationType;

        if (
            classificationType === ClassificationType.DynamicDemo ||
            classificationType === ClassificationType.Workshop
        ) {
            companyId = this.initData.user.companyId;
        }

        const partnerInfo = await this.api
            .get<PartnerInformationServer>(
                `/api/v4/companies/${companyId}/companysettings/partnerinfo`
            )
            .then(([response]) => response);

        return this.transform.transformPartnerInformationServerToClient(
            partnerInfo
        );
    }

    /**
     * This method is used to get partner information for the current company.
     * It uses the v5 API endpoint and returns a transformed PartnerInformation object.
     */
    getPartnerInformationV5(): Observable<PartnerInformation> {
        let companyId = this.userProfile.currentCompany;
        const classificationType =
            this.moduleAccess.moduleAccessConfig.accountClassification
                .classificationType;

        if (
            classificationType === ClassificationType.DynamicDemo ||
            classificationType === ClassificationType.Workshop
        ) {
            companyId = this.initData.user.companyId;
        }

        return from(
            this.api.get<PartnerInformationServer>(
                `/api/v5/companies/${companyId}/companysettings/partnerinfo`,
                null,
                null,
                PI?.appSettings?.companySettingsApiUrl
            )
        ).pipe(
            map(([response]) =>
                this.transform.transformPartnerInformationServerToClient(
                    response
                )
            )
        );
    }
}
